/*
 * Copyright (C) 2026 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { Component, Inject, NgZone, OnInit } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { CARDS_API_DATA_SERVICE_PROVIDER } from '../../../page-modules/programs/services/create/core/cards-api-data.service';
import { CARDS_DATA_SERVICE, CardsDataService } from '../../../page-modules/programs/services/create/core/cards-data.service';
import { RedirectHelper } from '../../../page-modules/resource/store/editor/content/helpers/redirect.helper';
import { AddContentCardFactoryService } from '../../../shared/components-v3/content-builder/add-content-dialog/add-content-card-factory.service';
import { CardPrefillType } from '../../../shared/components-v3/content-builder/add-content-dialog/add-content-catalog.model';
import { GlobalLaunchHelper } from '../../helpers/global-launch.helper';
import { AccessTokenService } from '../../services/access-token.service';
import { USER_IS_LOGGED_IN } from '../../services/api-basic-auth.service';
import { AUTH_SERVICE, BasicAuthService } from '../../services/basic-auth.service';
import { LtiAuthService } from '../../services/lti-auth.service';
import * as UserAuthActions from '../../store/user-auth.actions';
import { UserAuthState } from '../../store/user-auth.state';

const SETUP_SECTIONS: Record<string, string> = {
  overview: '',
  marking: 'marking',
  gradebook: 'gradebook',
  skills: 'skills',
  badges: 'badges',
};

const CARD_SEGMENTS: Record<string, string> = {
  EVENT: 'events',
  ASSESSMENT: 'assessments',
};

const CARD_PREFILLS: CardPrefillType[] = ['QUIZ', 'TEXTBOX', 'USER_FILE_UPLOAD', 'CHECKBOX', 'BOOKING'];

@Component({
  template: '',
  providers: [CARDS_API_DATA_SERVICE_PROVIDER, AddContentCardFactoryService],
})
export class MoodleLaunchComponent implements OnInit {
  constructor(
    private store: Store,
    private activatedRoute: ActivatedRoute,
    @Inject(AUTH_SERVICE) private authService: BasicAuthService,
    private router: Router,
    private ngZone: NgZone,
    private readonly accessTokenService: AccessTokenService,
    private readonly ltiAuthService: LtiAuthService,
    @Inject(CARDS_DATA_SERVICE) private readonly cardsDataService: CardsDataService,
    private readonly cardFactory: AddContentCardFactoryService,
  ) {
    this.accessTokenService.removeAccessToken();
    this.ltiAuthService.removeLtiRequestId();
    this.store.dispatch(new UserAuthActions.SetGlobalLmsLaunch(false));
  }

  ngOnInit() {
    const requestId = this.activatedRoute.snapshot.queryParamMap.get('requestId');
    if (!requestId) {
      this.redirectTo('/lti/registration/error');
      return;
    }
    this.authService.getLtiToken(requestId, false).subscribe({
      next: ({ isSuccess, value }) => {
        if (!isSuccess) {
          this.redirectTo('/lti/registration/error');
          return;
        }
        this.accessTokenService.saveAccessToken(value.access_token);
        localStorage.setItem(USER_IS_LOGGED_IN, 'true');
        this.store
          .dispatch([
            new UserAuthActions.FetchUserDetails(),
            new UserAuthActions.RedirectToAuthenticatedAppSuccess(),
            new UserAuthActions.GetHomePageTitleAndUri(),
          ])
          .pipe(
            switchMap(() => this.prepareNewCard(this.activatedRoute.snapshot.queryParamMap)),
            switchMap(() => this.destination(this.activatedRoute.snapshot.queryParamMap)),
          )
          .subscribe((url) => this.redirectTo(url));
      },
      error: () => this.redirectTo('/lti/registration/error'),
    });
  }

  private destination(parameters: ParamMap): Observable<string> {
    const content = encodeURIComponent(parameters.get('content') ?? '');
    switch (parameters.get('target')) {
      case 'COURSE':
        return of(`/courses/${content}/content`);
      case 'PROGRAM':
        return of(`/programs/${content}/content`);
      case 'PLAYLIST':
        return of(`/playlists/${content}`);
      case 'ASSIGNMENT':
        return of(
          this.isStaff()
            ? `/lti/course/setup/${content}/marking/${encodeURIComponent(parameters.get('assignment') ?? '')}`
            : `/courses/${content}/assignments/${encodeURIComponent(parameters.get('assignmentUri') ?? '')}`,
        );
      case 'CARD': {
        const segment = CARD_SEGMENTS[parameters.get('cardType')] ?? 'cards';
        const path = `/courses/${content}/${segment}/${encodeURIComponent(parameters.get('card') ?? '')}`;
        return of(this.isStaff() && parameters.get('edit') === '1' ? `${path}/edit` : `${path}/content`);
      }
      case 'SETUP': {
        const section = SETUP_SECTIONS[parameters.get('section')] ?? SETUP_SECTIONS.overview;
        return of(`/lti/course/setup/${content}${section ? `/${section}` : ''}`);
      }
      case 'PROFILE':
        return GlobalLaunchHelper.destination(this.store);
      default:
        return of(this.store.selectSnapshot(UserAuthState.homePageUri));
    }
  }

  private prepareNewCard(parameters: ParamMap): Observable<unknown> {
    const cardId = parameters.get('cardId');
    const prefill = parameters.get('prefill') as CardPrefillType;
    const headline = parameters.get('headline')?.trim() || undefined;
    if (parameters.get('target') !== 'CARD' || !cardId || !this.isStaff() || (!headline && !CARD_PREFILLS.includes(prefill))) {
      return of(undefined);
    }
    return this.cardsDataService.getCardById(cardId).pipe(
      switchMap(({ isSuccess, value }) => {
        const section = value?.content?.sections?.[0];
        if (!isSuccess || !section || section.dynamicContent?.length) {
          return of(undefined);
        }
        if (CARD_PREFILLS.includes(prefill)) {
          return this.cardFactory.prefillCard(cardId, section.uid, prefill, headline);
        }
        return value.content.headline ? of(undefined) : this.cardsDataService.updateCardHeadline(cardId, headline);
      }),
      map(() => undefined),
      catchError(() => of(undefined)),
    );
  }

  private isStaff(): boolean {
    return (
      this.store.selectSnapshot(UserAuthState.userHasInstructorRole) ||
      this.store.selectSnapshot(UserAuthState.userHasAdminRole) ||
      this.store.selectSnapshot(UserAuthState.userHasSuperAdminRole)
    );
  }

  private redirectTo(url: string) {
    RedirectHelper.redirectByUrl(this.ngZone, this.router, this.activatedRoute, url, GlobalLaunchHelper.redirectExtras(url));
  }
}
